<template>
  <q-header elevated class="app-header no-shadow bg-white">
    <q-toolbar class="grid full-height no-shadow q-px-md">
      <div class="header-left flex items-center no-wrap">
        <app-logo />
        <header-section-nav class="header-section-nav" />
        <breadcrumbs />
      </div>

      <div class="actions flex items-center">
        <div v-if="isCompaniesIndex" class="btn-container">
          <read-only-btn
            icon-right="add_business"
            label="Create company"
            no-caps
            unelevated
            color="cta"
            class="create-btn medium-radius q-mr-md text-18 text-primary"
            padding=".4rem 1.5rem .4rem 1.75rem"
            :disable="isLoading"
            @click="showCreateModal = true"
          />
        </div>
        <div v-if="isCompanyEdit" class="btn-container">
          <q-btn
            type="submit"
            :color="'brand'"
            :label="isDirtyMap[currentStep] ? 'Save changes' : 'All changes saved'"
            :icon="isDirtyMap[currentStep] ? 'cloud_upload' : 'cloud_done'"
            :outline="!isDirtyMap[currentStep]"
            no-caps
            unelevated
            :disabled="!isDirtyMap[currentStep]"
            :loading="isUpdating"
            padding=".4rem 1.5rem .4rem 1.75rem"
            class="save-btn btn-shadow medium-radius q-mr-md text-16 text-font-medium"
            @click="handleSubmit()"
          >
            <q-tooltip v-if="!isDirtyMap[currentStep]">
              No changes have been made to this section.
            </q-tooltip>
          </q-btn>
        </div>
        <div class="btn-container notifications-btn-container q-pr-md">
          <q-btn
            round
            outline
            :color="isDrawerOpen ? 'primary' : 'secondary'"
            class="notification-btn"
            @click="setDrawerOpen(!isDrawerOpen)"
          >
            <q-icon
              name="notifications"
              size="24px"
              :color="isDrawerOpen ? 'primary' : 'secondary'"
            />
            <q-badge
              v-if="notificationCount > 0"
              color="warning"
              floating
              class="notification-badge"
            >
              {{ notificationCount }}
            </q-badge>
          </q-btn>
        </div>
        <div class="btn-container">
          <user-menu />
        </div>
      </div>
    </q-toolbar>

    <create-modal
      v-model:show="showCreateModal"
      type="company"
      :is-loading="isCreatingCompany"
      :on-submit-callback="onCreateCompany"
    />
  </q-header>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useCompanyForm } from 'src/composables/app/useCompanyForm'
import { useConfirmUpdate } from 'src/composables/app/useConfirmUpdate'
import { useCompaniesQuery } from 'src/composables/companies/useCompaniesQuery'
import { useCreateCompanyMutation } from 'src/composables/company/useCreateCompanyMutation'

import { ROUTES } from 'src/services/consts/routes.const'

import ReadOnlyBtn from 'src/components/common/ReadOnlyBtn.vue'
import AppLogo from 'src/components/common/AppLogo.vue'
import UserMenu from 'src/components/layout/UserMenu.vue'
import CreateModal from 'src/components/common/CreateModal.vue'
import Breadcrumbs from 'src/components/common/Bread-crumbs.vue'
import HeaderSectionNav from 'src/components/layout/HeaderSectionNav.vue'

const route = useRoute()
const router = useRouter()

// Use the companies composables
const { isLoading } = useCompaniesQuery()
const { createCompany, isCreatingCompany, showCreateModal } = useCreateCompanyMutation()
const { submit, isDirtyMap, isUpdating } = useCompanyForm()
const { notificationCount, setDrawerOpen, isDrawerOpen } = useConfirmUpdate()

const isCompaniesIndex = computed(() => {
  return route.name === ROUTES.COMPANY && !route.params.id
})
const currentStep = computed(() => {
  return route.params.step
})

const isCompanyEdit = computed(() => {
  return route.name === ROUTES.COMPANY_EDIT
})

const onCreateCompany = async ({ name, fullAiAssessment, startSubprocessorList }) => {
  const company = await createCompany({
    companyName: name,
    fullAiAssessment,
    startSubprocessorList,
  })
  if (company?.id) {
    router.push({ name: ROUTES.APP })
  }
}
const handleSubmit = async () => {
  submit()
}
</script>

<style lang="scss">
@import '/src/css/setup/variables.scss';
.app-header {
  height: $header-height;
  z-index: 1000;
  border-bottom: 1px solid $clr-border;

  .q-toolbar {
    width: 100%;
    margin: 0 auto;
    max-width: $max-width-screen;
    grid-template-columns: minmax(0, 1fr) auto;
    justify-content: space-between;
  }

  .header-left {
    min-width: 0;
    overflow: hidden;
  }

  .q-layout__shadow:after {
    box-shadow: $main-shadow;
  }
  .header-section-nav {
    display: flex;
    align-items: center;
    min-height: $header-height;
    margin-left: 1rem;
    padding-inline: 1rem;
    border-left: 1px solid $clr-border;
    flex-shrink: 0;
  }
  .btn-container {
    display: flex;
    align-items: center;
    height: $header-height;
    border-left: 1px solid $clr-border;
    padding-inline-start: 1rem;
  }
  .save-btn {
    min-width: 220px !important;
  }

  .create-btn {
    box-shadow: $btn-shadow !important;
    border: 1px solid white;
    .q-icon {
      font-size: 1.5rem;
      margin-left: 0.5rem;
    }
  }

  .notification-btn {
    min-width: 40px;
    min-height: 40px;
    transition: 0.2s;

    &:hover {
      transform: scale(1.025);
    }

    .notification-badge {
      top: 0px;
      right: 0px;
      font-size: 12px;
      padding: 6px;
      min-width: 18px;
      height: 18px;
    }
  }
}
</style>
